import { IntlShape } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { validationErrorsToSectionErrorMap } from '@/components/common/Sidebar/validation.helper';
import { hasContentCategoriesPermission } from '@/components/Targeting/ContentCategories/helpers/hasContentCategoriesPermission.helper';
import { getPermittedMediaQuality } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/getPermittedMediaQuality.helper';
import { hasMediaQualityPermission } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/hasMediaQualityPermission.helper';
import { getDotStatusesByTargeting } from './getDotStatusesByTargeting.helper';

import type { ValidationErrors } from 'sonata-react-components';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { DcoInterface } from 'sonata-static-storage/lib/schemas/dco/dco.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { SidebarConfig, SidebarMainItemConfig } from '@/components/common/Sidebar/sidebar.types';
import type { DoohAudience } from '@/services/doohAudience.service';
import type { FormSection, FormSubsection } from '@/types/formSections.types';
import type { AttributionType, ListType } from '../../MultiFlightMap/multiFlightMap.types';

type SidebarParams = {
  activeSubsection: string | null;
  areLocationsOrScreensConfigured: boolean;
  attributionType?: AttributionType;
  contextualBidding?: ContextualBiddingInterface<any>;
  doohAudience?: DoohAudience;
  entityType: MultiFlightEntityType;
  formatMessage: IntlShape['formatMessage'];
  handleSectionClick: (section: FormSection) => () => void;
  handleSubsectionClick: (subsection: FormSubsection) => () => void;
  entityTargeting: TargetingBaseType;
  isContentTargetingEnabled: boolean;
  isContextTargetingChecked: boolean;
  isCreativesChecked: boolean;
  isCreativesEnabled: boolean;
  isDigital: boolean;
  isDooh: boolean;
  isLocationsSectionChecked: boolean;
  isLocationsSectionEnabled: boolean;
  isMainSettingsSectionChecked: boolean;
  list: ListType;
  sectionRef: React.RefObject<{ scrollToSection: (sectionId: string) => void }>;
  setActiveSubsection: (activeSubsection: FormSubsection | null) => void;
  setSelectedSection: (section: FormSection | null) => void;
  shouldShowValidationErrors: boolean;
  dco?: DcoInterface;
  draftErrors?: ValidationErrors;
  errors?: ValidationErrors;
  pixels?: unknown[];
  type?: CampaignFamilyEnum;
  userJSON: any;
  userRoles: UserRoleInterface[];
};

export function getSidebarConfig({
  activeSubsection,
  areLocationsOrScreensConfigured,
  attributionType,
  contextualBidding,
  entityTargeting,
  entityType,
  formatMessage,
  handleSectionClick,
  handleSubsectionClick,
  isContentTargetingEnabled,
  isContextTargetingChecked,
  isCreativesChecked,
  isCreativesEnabled,
  isDigital,
  isDooh,
  isLocationsSectionChecked,
  isLocationsSectionEnabled,
  isMainSettingsSectionChecked,
  list,
  shouldShowValidationErrors,
  dco,
  doohAudience,
  draftErrors,
  errors,
  pixels,
  type,
  userJSON,
  userRoles
}: SidebarParams): SidebarConfig<FormSection> {
  const dotStatuses = getDotStatusesByTargeting(
    { ...entityTargeting, mediaQuality: getPermittedMediaQuality(entityTargeting.mediaQuality, userJSON, userRoles) },
    contextualBidding,
    type,
    errors,
    doohAudience
  );

  const errorMap = shouldShowValidationErrors
    ? validationErrorsToSectionErrorMap({
        ...errors,
        ...(entityType !== 'insertionOrder' &&
          !areLocationsOrScreensConfigured && {
            [isDooh ? 'screens' : 'locations']: {
              code: 'custom',
              message: 'ioSettings.errors.locations',
              type: 'custom'
            }
          })
      })
    : validationErrorsToSectionErrorMap(draftErrors);

  // Helper function to get error count for a subsection
  const getSubsectionErrorCount = (sectionId: FormSection, subsectionId: FormSubsection): number => {
    return errorMap[sectionId]?.subsections[subsectionId] ?? 0;
  };

  const attributionPOISubsection = {
    active: list === 'venues' && activeSubsection === 'attributionPOIs',
    disabled: attributionType === 'footfall',
    errorCount: getSubsectionErrorCount('locations', 'attributionPOIs'),
    icon: 'fa-regular fa-location-dot',
    id: 'attributionPOIs',
    onSubsectionClick: handleSubsectionClick('attributionPOIs'),
    text: formatMessage({
      defaultMessage: 'Attribution by POIs',
      id: 'ioSettings.sidebar.attributionPOIs'
    })
  };

  const proximityPointsSubsection = {
    active: list === 'venues' && activeSubsection === 'proximityPoints',
    disabled: attributionType === 'driveToStore',
    errorCount: getSubsectionErrorCount('locations', 'proximityPoints'),
    icon: 'fa-regular fa-circle-dot',
    id: 'proximityPoints',
    onSubsectionClick: handleSubsectionClick('proximityPoints'),
    text: formatMessage({
      defaultMessage: 'Attribution by Proximity',
      id: 'ioSettings.sidebar.proximityPoints'
    })
  };

  return [
    {
      checked: isMainSettingsSectionChecked,
      children: [
        {
          active: activeSubsection === 'mediaChannel',
          errorCount: getSubsectionErrorCount('mainSettings', 'mediaChannel'),
          id: 'mediaChannel',
          onSubsectionClick: handleSubsectionClick('mediaChannel'),
          text: formatMessage({
            defaultMessage: 'Media Channel',
            id: 'ioSettings.sidebar.mediaChannel'
          }),
          visibility: entityType === 'insertionOrder'
        },
        {
          active: activeSubsection === 'ioSettings',
          errorCount: getSubsectionErrorCount('mainSettings', 'ioSettings'),
          id: 'ioSettings',
          onSubsectionClick: handleSubsectionClick('ioSettings'),
          text: formatMessage({
            defaultMessage: `${entityType === 'insertionOrder' ? 'IO' : 'LI'} Settings`,
            id: `ioSettings.sidebar.ioSettings.${entityType}`
          }),
          visibility: true
        },
        ...(isDigital
          ? [
              {
                active: activeSubsection === 'advertisingPlatform',
                errorCount: getSubsectionErrorCount('mainSettings', 'advertisingPlatform'),
                id: 'advertisingPlatform',
                onSubsectionClick: handleSubsectionClick('advertisingPlatform'),
                text: formatMessage({
                  defaultMessage: 'Advertising Platform',
                  id: 'ioSettings.sidebar.advertisingPlatform'
                }),
                visibility: true
              }
            ]
          : []),
        {
          active: activeSubsection === 'flightSettings',
          errorCount: getSubsectionErrorCount('mainSettings', 'flightSettings'),
          id: 'flightSettings',
          onSubsectionClick: handleSubsectionClick('flightSettings'),
          text: formatMessage({
            defaultMessage: 'Flights Settings',
            id: 'ioSettings.sidebar.flightsSettings'
          }),
          visibility: entityType === 'insertionOrder'
        },
        {
          active: activeSubsection === 'schedulingDistribution',
          errorCount: getSubsectionErrorCount('mainSettings', 'schedulingDistribution'),
          id: 'schedulingDistribution',
          onSubsectionClick: handleSubsectionClick('schedulingDistribution'),
          text: formatMessage({
            defaultMessage: 'Scheduling & Distribution',
            id: 'ioSettings.sidebar.schedulingDistribution'
          }),
          visibility: true
        },
        ...(!isDooh && userJSON?.mediaTypes?.length > 1
          ? [
              {
                active: activeSubsection === 'campaignMarketplace',
                errorCount: getSubsectionErrorCount('mainSettings', 'campaignMarketplace'),
                id: 'campaignMarketplace',
                onSubsectionClick: handleSubsectionClick('campaignMarketplace'),
                text: formatMessage({
                  defaultMessage: 'Campaign Marketplace',
                  id: 'ioSettings.sidebar.campaignMarketplace'
                }),
                visibility: true
              }
            ]
          : []),
        {
          active: activeSubsection === 'budgetAndGoals',
          errorCount: getSubsectionErrorCount('mainSettings', 'budgetAndGoals'),
          id: 'budgetAndGoals',
          onSubsectionClick: handleSubsectionClick('budgetAndGoals'),
          text: formatMessage({
            defaultMessage: 'Budget & Goals',
            id: 'ioSettings.sidebar.budgetAndGoals'
          }),
          visibility: entityType === 'lineItem'
        },
        {
          active: activeSubsection === 'pricingModel',
          errorCount: getSubsectionErrorCount('mainSettings', 'pricingModel'),
          id: 'pricingModel',
          onSubsectionClick: handleSubsectionClick('pricingModel'),
          text: formatMessage({
            defaultMessage: 'Pricing Model',
            id: 'ioSettings.sidebar.pricingModel'
          }),
          visibility: true
        }
      ].filter(i => i.visibility),
      enabled: Boolean(type),
      icon: 'fa-solid fa-gear',
      id: 'mainSettings',
      onSectionClick: handleSectionClick('mainSettings'),
      text: formatMessage({
        defaultMessage: 'Main Settings',
        id: 'ioSettings.sidebar.mainSettings'
      })
    },
    {
      checked: isLocationsSectionChecked,
      children: !isDooh
        ? [
            {
              active: list === 'locations',
              errorCount: getSubsectionErrorCount('locations', 'activationAreas'),
              icon: 'fa-sharp fa-regular fa-square-dashed',
              id: 'activationAreas',
              onSubsectionClick: handleSubsectionClick('activationAreas'),
              text: formatMessage({
                defaultMessage: 'Activation Areas',
                id: 'ioSettings.sidebar.activationAreas'
              })
            },
            attributionPOISubsection,
            proximityPointsSubsection
          ]
        : [
            {
              active: list === 'screens',
              errorCount: getSubsectionErrorCount('screens', 'screensSubsection'),
              icon: 'fa-kit fa-dooh',
              id: 'screensSubsection',
              onSubsectionClick: handleSubsectionClick('screensSubsection'),
              text: formatMessage({
                defaultMessage: 'Screens',
                id: 'ioSettings.sidebar.screens'
              })
            },
            attributionPOISubsection,
            proximityPointsSubsection
          ],
      enabled: isLocationsSectionEnabled,
      icon: 'fa-solid fa-earth-americas',
      id: isDooh ? 'screens' : 'locations',
      onSectionClick: handleSectionClick(isDooh ? 'screens' : 'locations'),
      text: isDooh
        ? formatMessage({
            defaultMessage: 'Screens',
            id: 'ioSettings.sidebar.screens'
          })
        : formatMessage({
            defaultMessage: 'Locations',
            id: 'ioSettings.sidebar.locations'
          })
    } as SidebarMainItemConfig<FormSection>,
    {
      checked: isContextTargetingChecked,
      children: [
        {
          items: [
            {
              active: activeSubsection === 'demography',
              dotStatus: dotStatuses.demography,
              errorCount: getSubsectionErrorCount('contextTargeting', 'demography'),
              id: 'demography',
              onSubsectionClick: handleSubsectionClick('demography'),
              show: true,
              text: isDooh
                ? formatMessage({
                    defaultMessage: 'Audience Targeting',
                    id: 'ioSettings.sidebar.doohAudienceTargeting'
                  })
                : formatMessage({
                    defaultMessage: 'Demography',
                    id: 'ioSettings.sidebar.demography'
                  })
            },
            {
              active: activeSubsection === 'retargeting',
              dotStatus: dotStatuses.retargeting,
              errorCount: getSubsectionErrorCount('contextTargeting', 'retargeting'),
              id: 'retargeting',
              onSubsectionClick: handleSubsectionClick('retargeting'),
              show: !isDooh && checkUserResourcePermissions('retargetingSection', userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Retargeting',
                id: 'ioSettings.sidebar.retargeting'
              })
            },
            {
              active: activeSubsection === 'audienceTargeting',
              dotStatus: dotStatuses.audienceTargeting,
              errorCount: getSubsectionErrorCount('contextTargeting', 'audienceTargeting'),
              id: 'audienceTargeting',
              onSubsectionClick: handleSubsectionClick('audienceTargeting'),
              show:
                !isDooh &&
                (checkUserResourcePermissions('audienceThirdParty', userJSON, userRoles) ||
                  checkUserResourcePermissions('audienceFirstParty', userJSON, userRoles) ||
                  checkUserResourcePermissions('audienceAffinity', userJSON, userRoles) ||
                  checkUserResourcePermissions('audienceCustom', userJSON, userRoles)),
              text: formatMessage({
                defaultMessage: 'Audience Targeting',
                id: 'ioSettings.sidebar.audienceTargeting'
              })
            },
            {
              active: activeSubsection === 'deviceTargeting',
              dotStatus: dotStatuses.deviceTargeting,
              errorCount: getSubsectionErrorCount('contextTargeting', 'deviceTargeting'),
              id: 'deviceTargeting',
              onSubsectionClick: handleSubsectionClick('deviceTargeting'),
              show: !isDooh && checkUserResourcePermissions('deviceIdImporter', userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Device Targeting',
                id: 'ioSettings.sidebar.deviceTargeting'
              })
            }
          ],
          label: formatMessage({
            defaultMessage: 'Audience & Users',
            id: 'ioSettings.sidebar.group.audienceUsers'
          })
        },
        {
          items: [
            {
              active: activeSubsection === 'mobileDevicesConnectivity',
              dotStatus: dotStatuses.mobileDevicesConnectivity,
              errorCount: getSubsectionErrorCount('contextTargeting', 'mobileDevicesConnectivity'),
              id: 'mobileDevicesConnectivity',
              onSubsectionClick: handleSubsectionClick('mobileDevicesConnectivity'),
              show: !isDooh,
              text: formatMessage({
                defaultMessage: 'Mobile Devices & Connectivity',
                id: 'ioSettings.sidebar.mobileDevicesConnectivity'
              })
            },
            {
              active: activeSubsection === 'supplySource',
              dotStatus: dotStatuses.supplySource,
              errorCount: getSubsectionErrorCount('contextTargeting', 'supplySource'),
              id: 'supplySource',
              onSubsectionClick: handleSubsectionClick('supplySource'),
              show: !isDooh && checkUserResourcePermissions('supplySource', userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Supply Source',
                id: 'ioSettings.sidebar.supplySource'
              })
            },
            {
              active: activeSubsection === 'appMarketplace',
              dotStatus: dotStatuses.appMarketplace,
              errorCount: getSubsectionErrorCount('contextTargeting', 'appMarketplace'),
              id: 'appMarketplace',
              onSubsectionClick: handleSubsectionClick('appMarketplace'),
              show: true,
              text: formatMessage({
                defaultMessage: 'App & Site Marketplace',
                id: 'ioSettings.sidebar.appMarketplace'
              })
            }
          ],
          label: formatMessage({
            defaultMessage: 'Technology & Supply',
            id: 'ioSettings.sidebar.group.technologySupply'
          })
        },
        {
          items: [
            {
              active: activeSubsection === 'appSiteCategories',
              dotStatus: dotStatuses.appSiteCategories,
              errorCount: getSubsectionErrorCount('contextTargeting', 'appSiteCategories'),
              id: 'appSiteCategories',
              onSubsectionClick: handleSubsectionClick('appSiteCategories'),
              show: !isDooh,
              text: formatMessage({
                defaultMessage: 'App & Site Categories',
                id: 'ioSettings.sidebar.appSiteCategories'
              })
            },
            {
              active: activeSubsection === 'appSiteChannel',
              dotStatus: dotStatuses.appSiteChannel,
              errorCount: getSubsectionErrorCount('contextTargeting', 'appSiteChannel'),
              id: 'appSiteChannel',
              onSubsectionClick: handleSubsectionClick('appSiteChannel'),
              show: !isDooh,
              text: formatMessage({
                defaultMessage: 'App & Site Channel',
                id: 'ioSettings.sidebar.appSiteChannel'
              })
            },
            {
              active: activeSubsection === 'contentCategories',
              dotStatus: dotStatuses.contentCategories,
              errorCount: getSubsectionErrorCount('contextTargeting', 'contentCategories'),
              id: 'contentCategories',
              onSubsectionClick: handleSubsectionClick('contentCategories'),
              show: !isDooh && hasContentCategoriesPermission(userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Content Categories',
                id: 'ioSettings.sidebar.contentCategories'
              })
            },
            {
              active: activeSubsection === 'temperatureAndWeather',
              dotStatus: dotStatuses.temperatureAndWeather,
              errorCount: getSubsectionErrorCount('contextTargeting', 'temperatureAndWeather'),
              id: 'temperatureAndWeather',
              onSubsectionClick: handleSubsectionClick('temperatureAndWeather'),
              show: true,
              text: formatMessage({
                defaultMessage: 'Temperature And Weather',
                id: 'ioSettings.sidebar.temperatureAndWeather'
              })
            },
            {
              active: activeSubsection === 'contextualBidding',
              dotStatus: dotStatuses.contextualBidding,
              errorCount: getSubsectionErrorCount('contextTargeting', 'contextualBidding'),
              id: 'contextualBidding',
              onSubsectionClick: handleSubsectionClick('contextualBidding'),
              show: true,
              text: formatMessage({
                defaultMessage: 'Contextual Bidding',
                id: 'ioSettings.sidebar.contextualBidding'
              })
            }
          ],
          label: formatMessage({
            defaultMessage: 'Context & Environment',
            id: 'ioSettings.sidebar.group.contextEnvironment'
          })
        },
        {
          items: [
            {
              active: activeSubsection === 'locationQualityIndexes',
              dotStatus: dotStatuses.locationQualityIndexes,
              errorCount: getSubsectionErrorCount('contextTargeting', 'locationQualityIndexes'),
              id: 'locationQualityIndexes',
              onSubsectionClick: handleSubsectionClick('locationQualityIndexes'),
              show: !isDooh && checkUserResourcePermissions('lqi', userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Location Quality Indexes',
                id: 'ioSettings.sidebar.locationQualityIndexes'
              })
            },
            {
              active: activeSubsection === 'mediaQualityAssurance',
              dotStatus: dotStatuses.mediaQualityAssurance,
              errorCount: getSubsectionErrorCount('contextTargeting', 'mediaQualityAssurance'),
              id: 'mediaQualityAssurance',
              onSubsectionClick: handleSubsectionClick('mediaQualityAssurance'),
              show: !isDooh && hasMediaQualityPermission(userJSON, userRoles),
              text: formatMessage({
                defaultMessage: 'Media Quality Assurance System',
                id: 'ioSettings.sidebar.mediaQualityAssurance'
              })
            }
          ],
          label: formatMessage({
            defaultMessage: 'Quality & Brand Safety',
            id: 'ioSettings.sidebar.group.qualityBrandSafety'
          })
        }
      ],
      enabled: isContentTargetingEnabled,
      icon: 'fa-kit fa-solid-fa-audience-centric',
      id: 'contextTargeting',
      onSectionClick: handleSectionClick('contextTargeting'),
      text: formatMessage({
        defaultMessage: 'Context & Targeting',
        id: 'ioSettings.sidebar.contextTargeting'
      })
    },
    {
      checked: isCreativesChecked,
      children: [
        {
          active: activeSubsection === 'adCreatives',
          errorCount: getSubsectionErrorCount('creatives', 'adCreatives'),
          id: 'adCreatives',
          onSubsectionClick: handleSubsectionClick('adCreatives'),
          text: formatMessage({
            defaultMessage: 'Ad Creatives',
            id: 'ioSettings.sidebar.creatives.items.adCreatives'
          })
        },
        {
          active: activeSubsection === 'conversionTracking',
          dotStatus: pixels && pixels.length > 0 ? 'success' : null,
          errorCount: getSubsectionErrorCount('creatives', 'conversionTracking'),
          id: 'conversionTracking',
          onSubsectionClick: handleSubsectionClick('conversionTracking'),
          show: !isDooh && checkUserResourcePermissions('campaignPixels', userJSON, userRoles),
          text: formatMessage({
            defaultMessage: 'Conversion Tracking',
            id: 'ioSettings.sidebar.creatives.items.conversionTracking'
          })
        },
        {
          active: activeSubsection === 'contextualSettings',
          dotStatus: dco?.enabled === true ? 'success' : null,
          errorCount: getSubsectionErrorCount('creatives', 'contextualSettings'),
          id: 'contextualSettings',
          onSubsectionClick: handleSubsectionClick('contextualSettings'),
          text: formatMessage({
            defaultMessage: 'Dynamic Creative Optimization',
            id: 'ioSettings.sidebar.creatives.items.contextualSettings'
          })
        },
        {
          active: activeSubsection === 'contextualMacros',
          errorCount: getSubsectionErrorCount('creatives', 'contextualMacros'),
          id: 'contextualMacros',
          onSubsectionClick: handleSubsectionClick('contextualMacros'),
          show: !isDooh,
          text: formatMessage({
            defaultMessage: 'Contextual Macros',
            id: 'ioSettings.sidebar.creatives.items.contextualMacros'
          })
        }
      ],
      enabled: isCreativesEnabled,
      icon: 'fa-solid fa-image',
      id: 'creatives',
      onSectionClick: handleSectionClick('creatives'),
      text: formatMessage({
        defaultMessage: 'Creatives',
        id: 'ioSettings.sidebar.creatives.title'
      })
    } as SidebarMainItemConfig<FormSection>
  ];
}
